import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBaseCurrency } from '@/lib/session';

/**
 * WAVE 4D — THE EDITABLE EXCHANGE RATE, one component for the seven document
 * headers that freeze a rate (sales invoice, quotation, sales order, credit
 * note, bill, purchase order, purchase return).
 *
 * The rule it draws is the server's (`typedRatePatch` / `freezeRate` in
 * apps/api/src/platform/fx-rates.ts):
 *   - empty box  = the MARKET rate: the draft re-reads the rate table and the
 *                  freeze takes the newest row on or before the document date;
 *                  the placeholder and the hint show that figure.
 *   - a typed rate = stored on the draft (`rateOverride`) and the one kept at
 *                  confirmation; "Use market rate" clears it again.
 *   - a document in the company's own currency draws no rate at all (GEN-116:
 *     nothing converts) — the server would refuse anything but 1 anyway.
 *
 * Rates follow GEN-109a: units of the document's currency per ONE base unit,
 * four decimals.
 */

/** What the editor's saved document says about its rate (contract fields). */
export interface RateFieldDoc {
  currency: string;
  baseCurrency: string;
  exchangeRate: string | null;
  rateOverride: boolean;
  marketRate: string | null;
  marketRateOn: string | null;
}

/** The typed text, adopted from the saved document, and the two body spellings. */
export interface RateInput {
  text: string;
  setText: (value: string) => void;
  /** CREATE: a typed rate, or nothing (= the market rate). */
  createBody: () => { exchangeRate?: string };
  /**
   * PATCH: only when the box moved — a typed rate, or null to return to the
   * market rate. Unchanged sends nothing, so a posted document's notes-only
   * save never carries the key.
   */
  patchBody: () => { exchangeRate?: string | null };
}

const RATE_SHAPE = /^\d{1,11}(\.\d{1,4})?$/;

/** The text the box shows for a saved document: its typed rate, else empty. */
function adoptedText(doc: RateFieldDoc | undefined): string {
  return doc?.rateOverride === true && doc.exchangeRate !== null ? doc.exchangeRate : '';
}

export function useRateInput(doc: RateFieldDoc | undefined): RateInput {
  const saved = adoptedText(doc);
  const [text, setText] = useState(saved);
  // Re-adopt whenever the SAVED figure changes (a load, a save's response).
  useEffect(() => {
    setText(saved);
  }, [saved]);

  return {
    text,
    setText,
    createBody: () => (text.trim() === '' ? {} : { exchangeRate: text.trim() }),
    patchBody: () => {
      const typed = text.trim();
      if (typed === saved) return {};
      return { exchangeRate: typed === '' ? null : typed };
    },
  };
}

/** True when the box holds something the server would reject on shape alone. */
export function rateTextInvalid(text: string): boolean {
  const typed = text.trim();
  return typed !== '' && (!RATE_SHAPE.test(typed) || /^0+(\.0+)?$/.test(typed));
}

export function RateField({
  idPrefix,
  currency,
  doc,
  input,
  disabled,
}: {
  idPrefix: string;
  /** The document's currency (the partner's, before the first save). */
  currency: string;
  doc: RateFieldDoc | undefined;
  input: RateInput;
  /** Not a draft (or no permission): the frozen figure shows, read-only. */
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const sessionBase = useBaseCurrency();
  const base = doc?.baseCurrency ?? sessionBase;
  if (currency === '' || currency === base) return null;

  const id = `${idPrefix}-rate`;
  const hintId = `${id}-hint`;
  const market = doc?.marketRate ?? null;
  // A confirmed document shows the rate it froze; a draft shows what it was typed.
  const shown = disabled && doc?.exchangeRate ? doc.exchangeRate : input.text;
  const invalid = !disabled && rateTextInvalid(input.text);

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{t('fx.rateField', { base, currency })}</Label>
      <Input
        id={id}
        dir="ltr"
        inputMode="decimal"
        className="font-mono"
        value={shown}
        placeholder={market ?? ''}
        onChange={(event) => input.setText(event.target.value)}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={hintId}
      />
      <div id={hintId} className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
        {invalid ? (
          <span className="text-destructive">{t('fx.rateShape')}</span>
        ) : disabled ? (
          doc?.rateOverride === true ? <span>{t('fx.rateTyped')}</span> : null
        ) : market !== null ? (
          <span>
            {doc?.marketRateOn ? (
              <>
                {t('fx.marketRateAsOf')} <Ltr value={market} className="font-mono" />{' '}
                {t('fx.marketRateOn')} <Ltr value={doc.marketRateOn} />
              </>
            ) : (
              <>
                {t('fx.marketRateAsOf')} <Ltr value={market} className="font-mono" />
              </>
            )}
          </span>
        ) : doc !== undefined ? (
          <span>{t('fx.noMarketRate')}</span>
        ) : null}
        {!disabled && input.text.trim() !== '' ? (
          <button
            type="button"
            className="font-medium text-primary underline-offset-2 hover:underline"
            onClick={() => input.setText('')}
          >
            {t('fx.useMarketRate')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
